import { Button } from "$components/button/button";
import type { OptionType } from "$components/inputs";
import type { Invoice as RxInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import {
  isServerTimeUnknownError,
  serverTimeUnknownMessage,
  useServerTime,
} from "$global-providers/server-time-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { Invoice } from "$routes/_lib/billing-v2/invoice";
import { useLocationsContext } from "$routes/_lib/locations-context";
import { randomUniqueId } from "$routes/_lib/random-id";
import type { PriceList } from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceCard/types";
import { canPerformAction } from "$routes/patient/[:patientId]/_lib/invoices-v2/_lib/can-perform-action";
import { invoiceRecordFromDocument } from "$routes/patient/[:patientId]/_lib/invoices-v2/invoice-record-from-document";
import { FinaliseInvoiceModalV2 } from "$routes/patient/[:patientId]/_lib/invoices-v2/modals/finalise-invoice-modal-v2";
import { useFetchPatientInsuranceRxdb } from "$routes/patient/[:patientId]/_lib/invoices-v2/use-fetch-patient-insurance-rxdb";
import { useSavePatientInsuranceRxdb } from "$routes/patient/[:patientId]/_lib/invoices-v2/use-save-patient-insurance-rxdb";
import { yupResolver } from "@hookform/resolvers/yup";
import { InvoiceStatus, PatientType } from "elephant-web/gql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FormProvider,
  type Resolver,
  useForm,
  useWatch,
} from "react-hook-form";
import { useIntl } from "react-intl";
import type { RxDocument } from "rxdb";
import {
  advanceDraftFormBaseline,
  applyDraftInvoiceValues,
  DRAFT_FIELD_NAMES,
  type DraftInvoiceFormValues,
  type DraftInvoiceItemValue,
  draftAutosaveSchema,
  draftFinaliseSchema,
  draftInsurers,
  emptyDraftItem,
  insuranceNamesOnInvoice,
  isInpatient,
  membershipsOnInvoice,
  toDraftFormValues,
} from "./_lib/draft-invoice-form";
import { EncounterTypeRow } from "./_lib/encounter-type-row";
import { InpatientDetails } from "./_lib/inpatient-details";
import { InvoiceCardActions } from "./_lib/invoice-card-actions";
import { InvoiceLineItemCard } from "./_lib/invoice-line-item-card";
import type { InvoiceLineItemDraftRow } from "./_lib/invoice-line-item-row";
import { InvoiceLineItems, stacksLineItems } from "./_lib/invoice-line-items";
import { InvoiceStatusChip } from "./_lib/invoice-status-chip";
import { InvoiceTotals } from "./_lib/invoice-totals";
import {
  buildInvoiceView,
  isPrivateInsurerName,
  lineItemPayerOptions,
  tariffForChosenItem,
  tariffRevenueDepartmentId,
} from "./_lib/invoice-view";
import { PayersOnInvoice } from "./_lib/payers-on-invoice";
import { useDraftAutosave } from "./_lib/use-draft-autosave";
import { useInvoiceCardData } from "./_lib/use-invoice-card-data";
import { InvoiceCardHeader } from "./invoice-card-header";
import { InvoiceCardSection, useInvoiceCardLayout } from "./invoice-card-shell";

const SAVE_ERROR_MESSAGE =
  "Couldn't save this invoice — your edit is still pending";
const INVALID_MESSAGE = "Check the highlighted fields before finalising";
const INVALID_CLOSE_MESSAGE =
  "Finish or remove incomplete edits before closing";
const ADD_ITEM_LABEL = "＋ Add item";

const itemFieldNames = (index: number) =>
  ({
    name: `${DRAFT_FIELD_NAMES.items}.${index}.name`,
    department: `${DRAFT_FIELD_NAMES.items}.${index}.department`,
    supplier: `${DRAFT_FIELD_NAMES.items}.${index}.supplier`,
    quantity: `${DRAFT_FIELD_NAMES.items}.${index}.quantity`,
    payer: `${DRAFT_FIELD_NAMES.items}.${index}.payer`,
  }) as const;

export const DraftInvoiceCard: FC<{
  doc: RxDocument<RxInvoice>;
  patientId: string;
  priceList: PriceList;
  dateLabel: string;
  clinicianName?: string;
  onToggleExpanded: () => void;
}> = ({
  doc,
  patientId,
  priceList,
  dateLabel,
  clinicianName,
  onToggleExpanded,
}) => {
  const user = useUser();
  const { getCurrentTime } = useServerTime();
  const { formatMessage } = useIntl();
  const { width, isCompact } = useInvoiceCardLayout();
  const isStacked = stacksLineItems("draft", width);
  const { facilityLocations } = useLocationsContext();
  const cardData = useInvoiceCardData(priceList, doc.balance.currency);
  const {
    itemsById,
    itemOptions,
    departmentOptions,
    supplierOptions,
    formatMoney,
  } = cardData;

  const patientInsurance = useFetchPatientInsuranceRxdb(patientId);
  const { saveInsurance } = useSavePatientInsuranceRxdb(patientId);

  const [finaliseOpen, setFinaliseOpen] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  const initialItems = useMemo(
    () => toDraftFormValues(doc, {}, {}).items,
    [doc],
  );
  const persistedInsurers = useMemo(
    () => draftInsurers(initialItems, itemsById),
    [initialItems, itemsById],
  );
  const initialValues = useMemo(
    () =>
      toDraftFormValues(
        doc,
        membershipsOnInvoice(doc.payers, persistedInsurers),
        insuranceNamesOnInvoice(doc.payers, persistedInsurers),
      ),
    [doc, persistedInsurers],
  );
  const baselineRef = useRef(initialValues);
  const payerIdsByTariffIdRef = useRef(new Map<string, string>());
  const payerIdForTariffId = useCallback((tariffId: string): string => {
    const existing = payerIdsByTariffIdRef.current.get(tariffId);
    if (existing) return existing;
    const created = randomUniqueId();
    payerIdsByTariffIdRef.current.set(tariffId, created);
    return created;
  }, []);

  // Which insurer fields are required follows from the tariffs the lines are
  // on, so the finalise schema is built from the values being validated rather
  // than pinned to the roster the card mounted with.
  const resolver = useCallback<Resolver<DraftInvoiceFormValues>>(
    (values, context, options) =>
      (
        yupResolver(
          draftFinaliseSchema(draftInsurers(values.items, itemsById)),
        ) as unknown as Resolver<DraftInvoiceFormValues>
      )(values, context, options),
    [itemsById],
  );

  const formMethods = useForm<DraftInvoiceFormValues>({
    resolver,
    mode: "onTouched",
    defaultValues: initialValues,
  });
  const { control, getValues, setValue, formState, handleSubmit } = formMethods;
  const { isDirty } = formState;

  const [
    encounterType,
    ward,
    patientNumber,
    admissionDate,
    dischargeDate,
    items,
    memberships,
    insuranceNames,
  ] = useWatch({
    control,
    name: [
      DRAFT_FIELD_NAMES.encounterType,
      DRAFT_FIELD_NAMES.ward,
      DRAFT_FIELD_NAMES.patientNumber,
      DRAFT_FIELD_NAMES.admissionDate,
      DRAFT_FIELD_NAMES.dischargeDate,
      DRAFT_FIELD_NAMES.items,
      DRAFT_FIELD_NAMES.memberships,
      DRAFT_FIELD_NAMES.insuranceNames,
    ],
  });

  const values = useMemo<DraftInvoiceFormValues>(
    () => ({
      encounterType: encounterType ?? PatientType.Outpatient,
      ward: ward ?? "",
      patientNumber: patientNumber ?? "",
      admissionDate: admissionDate ?? "",
      dischargeDate: dischargeDate ?? "",
      items: items ?? [],
      memberships: memberships ?? {},
      insuranceNames: insuranceNames ?? {},
    }),
    [
      encounterType,
      ward,
      patientNumber,
      admissionDate,
      dischargeDate,
      items,
      memberships,
      insuranceNames,
    ],
  );

  const previewInvoice = useMemo(() => {
    const record = invoiceRecordFromDocument(doc);
    const persisted = Invoice.fromRecord(record).toRecord();
    try {
      return applyDraftInvoiceValues({
        record,
        baseline: baselineRef.current,
        values,
        itemsById,
        payerIdForTariffId,
      });
    } catch {
      return persisted;
    }
  }, [doc, itemsById, payerIdForTariffId, values]);

  const view = useMemo(
    () =>
      buildInvoiceView({
        ...cardData,
        invoice: previewInvoice,
        priceList,
        membershipByInsurerName: new Map(),
        fallbackItemDate: doc.meta.createdAt,
      }),
    [cardData, previewInvoice, priceList, doc.meta.createdAt],
  );

  const persist = useCallback(
    async (next: DraftInvoiceFormValues) => {
      try {
        const attemptedBaseline: DraftInvoiceFormValues = {
          ...baselineRef.current,
          items: baselineRef.current.items.map((item) => ({ ...item })),
          memberships: { ...baselineRef.current.memberships },
          insuranceNames: { ...baselineRef.current.insuranceNames },
        };
        const updatedAt = getCurrentTime().toISOString();
        await doc.incrementalModify((latest) => {
          return applyDraftInvoiceValues({
            record: latest as RxInvoice,
            baseline: attemptedBaseline,
            values: next,
            itemsById,
            payerIdForTariffId,
            updatedAt,
            updatedByUserId: user.id,
          });
        });
        baselineRef.current = advanceDraftFormBaseline({
          current: baselineRef.current,
          attempted: attemptedBaseline,
          saved: next,
        });

        const insurers = draftInsurers(next.items, itemsById);
        await saveInsurance(
          insurers.map((insurer) => ({
            type: insurer.name,
            insuranceDetails: {
              insuranceNumber: next.memberships[insurer.tariffId] ?? "",
            },
          })),
        );
      } catch (err) {
        toast.error(
          isServerTimeUnknownError(err)
            ? formatMessage(serverTimeUnknownMessage)
            : SAVE_ERROR_MESSAGE,
        );
        throw err;
      }
    },
    [
      doc,
      formatMessage,
      getCurrentTime,
      itemsById,
      payerIdForTariffId,
      saveInsurance,
      user.id,
    ],
  );

  const { schedule, cancelPending, flush } = useDraftAutosave(persist);

  useEffect(() => {
    if (!isDirty || !draftAutosaveSchema.isValidSync(values)) {
      cancelPending();
      return;
    }
    schedule(values);
  }, [cancelPending, isDirty, schedule, values]);

  // Pre-fill the insurer details the patient already has on file, without
  // marking the form dirty — nothing has been edited yet. Private cover is
  // registered against the patient as a scheme name rather than an insurer.
  useEffect(() => {
    const details = patientInsurance?.insuranceDetails;
    if (!details) return;
    for (const insurer of view.insurers) {
      if (insurer.name !== patientInsurance?.insuranceType) continue;
      const membershipField =
        `${DRAFT_FIELD_NAMES.memberships}.${insurer.tariffId}` as const;
      const insuranceNameField =
        `${DRAFT_FIELD_NAMES.insuranceNames}.${insurer.tariffId}` as const;
      if (details.insuranceNumber && !getValues(membershipField)) {
        baselineRef.current.memberships[insurer.tariffId] =
          details.insuranceNumber;
        setValue(membershipField, details.insuranceNumber, {
          shouldDirty: false,
        });
      }
      if (
        isPrivateInsurerName(insurer.name) &&
        details.schemeName &&
        !getValues(insuranceNameField)
      ) {
        baselineRef.current.insuranceNames[insurer.tariffId] =
          details.schemeName;
        setValue(insuranceNameField, details.schemeName, {
          shouldDirty: false,
        });
      }
    }
  }, [getValues, patientInsurance, setValue, view.insurers]);

  // Choosing the item on a line re-homes it — on a line just added as much as
  // on one being changed: the revenue department comes from the catalogue, and
  // the tariff from what the new item actually offers.
  const lineSnapshot = useRef(
    new Map<string, { name: string; insurerName: string | null }>(),
  );
  useEffect(() => {
    values.items.forEach((item, index) => {
      const previous = lineSnapshot.current.get(item.id);
      if (!previous || previous.name === item.name) return;
      const catalogueItem = itemsById.get(item.name);
      const tariffId =
        tariffForChosenItem(catalogueItem, previous.insurerName) ?? "";
      setValue(itemFieldNames(index).payer, tariffId, { shouldDirty: true });
      setValue(
        itemFieldNames(index).department,
        tariffRevenueDepartmentId(catalogueItem, tariffId) ?? "",
        { shouldDirty: true },
      );
    });

    lineSnapshot.current = new Map(
      values.items.map((item) => [
        item.id,
        {
          name: item.name,
          insurerName:
            itemsById
              .get(item.name)
              ?.payers?.find((tariff) => tariff.tariffId === item.payer)
              ?.name ?? null,
        },
      ]),
    );
  }, [itemsById, setValue, values.items]);

  const addItem = () => {
    const item = emptyDraftItem(randomUniqueId());
    setValue(
      DRAFT_FIELD_NAMES.items,
      [...(getValues(DRAFT_FIELD_NAMES.items) ?? []), item],
      { shouldDirty: true },
    );
    if (isStacked) setExpandedItemId(item.id);
  };

  const removeItem = (rowId: string) => {
    setValue(
      DRAFT_FIELD_NAMES.items,
      (getValues(DRAFT_FIELD_NAMES.items) ?? []).filter(
        (item: DraftInvoiceItemValue) => item.id !== rowId,
      ),
      { shouldDirty: true },
    );
  };

  const wardOptions = useMemo<OptionType[]>(() => {
    const options = facilityLocations.map((location) => ({
      value: location.name,
      label: location.name,
    }));
    return values.ward &&
      !options.some((option) => option.value === values.ward)
      ? [{ value: values.ward, label: values.ward }, ...options]
      : options;
  }, [facilityLocations, values.ward]);

  const rows = useMemo<InvoiceLineItemDraftRow[]>(() => {
    const linesById = new Map(view.lines.map((line) => [line.id, line]));
    return values.items.map((item, index) => {
      const line = linesById.get(item.id);
      if (line) {
        return {
          ...line.row,
          fieldNames: itemFieldNames(index),
          payerOptions: line.payerOptions,
        };
      }
      const catalogueItem = itemsById.get(item.name);
      const tariff = catalogueItem?.payers?.find(
        (candidate) => candidate.tariffId === item.payer,
      );
      return {
        id: item.id,
        name: catalogueItem?.name ?? "—",
        departmentLabel: item.department
          ? (cardData.departmentNameById.get(item.department) ?? "—")
          : "—",
        supplierLabel: "—",
        quantityLabel: item.quantity || "—",
        unitPriceLabel: "—",
        amountLabel: "—",
        payerLabel: tariff?.name ?? "—",
        payerKind:
          tariff?.name && tariff.name !== "Patient"
            ? ("insurer" as const)
            : ("self-pay" as const),
        fieldNames: itemFieldNames(index),
        payerOptions: lineItemPayerOptions(
          catalogueItem,
          cardData.toMajor,
          formatMoney,
        ),
      };
    });
  }, [
    cardData.departmentNameById,
    cardData.toMajor,
    formatMoney,
    itemsById,
    values.items,
    view.lines,
  ]);

  const canFinalise = canPerformAction(
    user.role,
    "finalise",
    InvoiceStatus.Draft,
  );

  const collapse = () => {
    if (isDirty && !draftAutosaveSchema.isValidSync(values)) {
      toast.error(INVALID_CLOSE_MESSAGE);
      return;
    }
    void flush()
      .then(onToggleExpanded)
      .catch(() => undefined);
  };

  return (
    <>
      <InvoiceCardHeader
        dateLabel={dateLabel}
        encounterTypeLabel={values.encounterType}
        clinicianName={clinicianName}
        amount={formatMoney(view.patientBalance)}
        note={
          isCompact
            ? "due from patient"
            : `due from patient · ${formatMoney(view.itemsBilled)} billed`
        }
        chip={<InvoiceStatusChip status={InvoiceStatus.Draft} />}
        onCollapse={collapse}
        collapseLabel="Collapse invoice"
      />

      <FormProvider {...formMethods}>
        <form
          aria-label="Draft invoice"
          // The stay fields are `required`, and the browser's own constraint
          // check would otherwise swallow the submit before the schema runs —
          // leaving the user with no feedback at all, since every field here
          // passes `hideHelper` and the invalid-submit toast is the only
          // reason the card ever shows.
          noValidate
          onSubmit={handleSubmit(
            () => setFinaliseOpen(true),
            () => toast.error(INVALID_MESSAGE),
          )}
        >
          <EncounterTypeRow
            fieldNames={{ encounterType: DRAFT_FIELD_NAMES.encounterType }}
            lineItemCount={values.items.length}
          />

          {isInpatient(values.encounterType) && (
            <InpatientDetails
              mode="editable"
              fieldNames={{
                ward: DRAFT_FIELD_NAMES.ward,
                patientNumber: DRAFT_FIELD_NAMES.patientNumber,
                admissionDate: DRAFT_FIELD_NAMES.admissionDate,
                dischargeDate: DRAFT_FIELD_NAMES.dischargeDate,
              }}
              wardOptions={wardOptions}
            />
          )}

          {isStacked ? (
            <InvoiceCardSection
              paddingTop={0}
              paddingBottom={0}
              disablePadX
              data-testid="invoicing.v2.card.lineItems"
            >
              {rows.map((row) => (
                <InvoiceLineItemCard
                  key={row.id}
                  variant="draft"
                  row={row}
                  itemOptions={itemOptions}
                  departmentOptions={departmentOptions}
                  supplierOptions={supplierOptions}
                  isExpanded={expandedItemId === row.id}
                  onToggleExpanded={() =>
                    setExpandedItemId(expandedItemId === row.id ? null : row.id)
                  }
                  onRemove={() => removeItem(row.id)}
                />
              ))}
            </InvoiceCardSection>
          ) : (
            <InvoiceLineItems
              variant="draft"
              rows={rows}
              itemOptions={itemOptions}
              departmentOptions={departmentOptions}
              supplierOptions={supplierOptions}
              onRemoveItem={removeItem}
              data-testid="invoicing.v2.card.lineItems"
            />
          )}

          <InvoiceCardSection
            paddingTop={12}
            paddingBottom={16}
            divider={false}
          >
            <Button color="secondary" size="small" onClick={addItem}>
              {ADD_ITEM_LABEL}
            </Button>
          </InvoiceCardSection>

          <PayersOnInvoice
            payers={view.payers}
            formatMoney={formatMoney}
            mode="editable"
            fieldNames={{
              memberships: DRAFT_FIELD_NAMES.memberships,
              insuranceNames: DRAFT_FIELD_NAMES.insuranceNames,
            }}
          />

          <InvoiceTotals
            mode="draft"
            itemsBilled={view.itemsBilled}
            coverage={view.coverage}
            patientBalance={view.patientBalance}
            formatMoney={formatMoney}
          />

          <InvoiceCardActions
            doc={doc}
            patientId={patientId}
            invoiceStatus={InvoiceStatus.Draft}
            mode="draft"
            priceList={priceList}
            payerNames={view.insurers.map((insurer) => insurer.name)}
            totalRefundableInPence={0}
            currency={String(doc.balance.currency)}
            primaryAction={
              canFinalise && (
                <Button type="submit" color="primary">
                  Finalise
                </Button>
              )
            }
          />
        </form>
      </FormProvider>

      {finaliseOpen && (
        <FinaliseInvoiceModalV2
          doc={doc}
          open
          onClose={() => setFinaliseOpen(false)}
          flushPendingSaves={flush}
        />
      )}
    </>
  );
};
